/**
 * Modular PDP — Ask AI widget (Claude via Cloudflare Worker).
 */
(function (win, doc) {
  'use strict';

  var DEFAULT_ENDPOINT = 'https://pdp-ask-ai-worker-v2.nnh-ai.workers.dev';
  var SESSION_LIMIT = 20;             // max questions per 24-hour window
  var SESSION_WINDOW_MS = 86400000;   // 24 hours in ms
  var SESSION_KEY = 'ask_ai_session';
  var MIN_INTERVAL_MS = 3000;         // min ms between submissions
  var lastAskTime = 0;

  function getSessionData() {
    try {
      var raw = win.localStorage.getItem(SESSION_KEY);
      if (!raw) return { count: 0, windowStart: Date.now() };
      var data = JSON.parse(raw);
      if (Date.now() - data.windowStart > SESSION_WINDOW_MS) {
        return { count: 0, windowStart: Date.now() };
      }
      return data;
    } catch (e) {
      return { count: 0, windowStart: Date.now() };
    }
  }

  function saveSessionData(data) {
    try { win.localStorage.setItem(SESSION_KEY, JSON.stringify(data)); } catch (e) {}
  }

  function pushEvent(name, detail) {
    win.dataLayer = win.dataLayer || [];
    win.dataLayer.push(Object.assign({ event: name }, detail || {}));
  }

  function stripHtml(value) {
    if (!value) return '';
    var el = doc.createElement('div');
    el.innerHTML = String(value);
    return (el.textContent || el.innerText || '').trim();
  }

  function parseJsonAttr(el, attr, fallback) {
    try {
      var raw = el.getAttribute(attr);
      if (!raw) return fallback;
      return JSON.parse(raw);
    } catch (e) {
      return fallback;
    }
  }

  function isProductUrl(url) {
    if (!url) return false;
    try {
      var parsed = new URL(url, win.location.origin);
      return parsed.pathname.indexOf('/products/') === 0;
    } catch (e) {
      return String(url).indexOf('/products/') !== -1;
    }
  }

  function wantsPairingIntent(question) {
    var q = String(question || '').toLowerCase();
    return /pair|combine|stack|add[- ]?on|together|complement|another product|what should i (add|take)|go(es)? well/.test(q);
  }

  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function inlineMd(str) {
    // Markdown links [text](url) — only allow internal /blogs/ and /products/ paths
    var linked = String(str).replace(/\[([^\]]+)\]\((\/(?:blogs|products)[^)]*)\)/g, function (_, text, url) {
      return '<a href="' + url + '" class="modular-pdp-ask-ai__answer-link">' + escHtml(text) + '</a>';
    });
    // Split on **bold** markers; odd indices are inside the markers
    var parts = linked.split(/\*\*([^*]+)\*\*/g);
    return parts
      .map(function (part, i) {
        // Don't double-escape already-rendered HTML from link step
        return i % 2 === 1 ? '<strong>' + escHtml(part) + '</strong>' : part;
      })
      .join('');
  }

  function formatAnswer(text) {
    var raw = String(text || '');
    var lines = raw.split('\n').map(function (l) { return l.trim(); }).filter(Boolean);

    var ITEM = 'modular-pdp-ask-ai__answer-item body-small';
    var HEADING = 'modular-pdp-ask-ai__answer-heading body-small font-weight-bold';
    var NEXT_STEP = 'modular-pdp-ask-ai__answer-nextstep body-small';

    var parts = lines.map(function (line) {
      // Section heading: a line that is ONLY **text** or **text:**
      if (/^\*\*[^*]+\*\*:?\s*$/.test(line)) {
        var headText = line.replace(/^\*\*/, '').replace(/\*\*:?\s*$/, '');
        return { cls: HEADING, html: escHtml(headText) };
      }

      // Explicit Next step line
      if (/^\*\*next\s*step/i.test(line)) {
        var nsRaw = line
          .replace(/^\*\*next\s*step[^*]*\*\*:?\s*/i, '')
          .replace(/^\*\*next\s*step[:\s]*/i, '')
          .trim();
        return { cls: NEXT_STEP, html: inlineMd(nsRaw) };
      }

      // Strip leading bullet characters (•, -, *) — CSS adds its own
      var stripped = line.replace(/^[•\-\*]\s+/, '').trim();
      return { cls: ITEM, html: inlineMd(stripped) };
    });

    // Fallback: if the last paragraph is a plain item, style it as the next step
    if (parts.length && parts[parts.length - 1].cls === ITEM) {
      parts[parts.length - 1].cls = NEXT_STEP;
    }

    return parts
      .map(function (p) { return '<p class="' + p.cls + '">' + p.html + '</p>'; })
      .join('');
  }

  function demoAnswer(question, productTitle) {
    var q = String(question || '').toLowerCase();
    var title = productTitle || 'this product';

    if (wantsPairingIntent(question)) {
      return (
        title +
        ' is your foundation product on this page.\n' +
        'Pairing depends on your primary goal; start one new product at a time.\n' +
        'Connect the live endpoint for personalized pairing suggestions from your configured products.\n' +
        'Next step: Ask a goal-specific pairing question after connecting the backend.'
      );
    }

    if (/how should i take|dosage|when should i take/.test(q)) {
      return (
        'Take ' +
        title +
        ' consistently at the same time each day.\n' +
        'Follow the label serving for 2-3 weeks before judging early changes.\n' +
        'Pair with a daily routine trigger (for example, breakfast) for consistency.\n' +
        'Next step: Follow the label directions and check with your healthcare provider if needed.'
      );
    }

    return (
      title +
      ' is designed to support your wellness goals as described on this page.\n' +
      'Answers are most accurate when the live AI endpoint is connected.\n' +
      'For medical decisions, consult your healthcare provider.\n' +
      'Next step: Follow the label directions and check with your healthcare provider if needed.'
    );
  }

  function initWidget(root) {
    if (!root || root.dataset.askAiReady === 'true') return;
    root.dataset.askAiReady = 'true';

    var endpoint = (root.getAttribute('data-endpoint') || '').trim() || DEFAULT_ENDPOINT;
    var candidates = parseJsonAttr(root, 'data-ask-ai-candidates', []);
    var candidateByHandle = {};
    candidates.forEach(function (c) {
      if (c && c.handle) candidateByHandle[c.handle] = c;
    });

    var form = root.querySelector('[data-ask-ai-form]');
    var input = root.querySelector('[data-ask-ai-input]');
    var submitBtn = root.querySelector('[data-ask-ai-submit]');
    var statusEl = root.querySelector('[data-ask-ai-status]');
    var responseWrap = root.querySelector('[data-ask-ai-response]');
    var responseLabel = root.querySelector('[data-ask-ai-response-label]');
    var answerEl = root.querySelector('[data-ask-ai-answer]');
    var blogLinkEl = root.querySelector('[data-ask-ai-blog-link]');
    var supportEl = root.querySelector('[data-ask-ai-support]');
    var recoEl = root.querySelector('[data-ask-ai-reco]');
    var nudgesEl = root.querySelector('[data-ask-ai-nudges]');
    var questionEl = root.querySelector('[data-ask-ai-question]');

    var quizUrl = root.getAttribute('data-quiz-url') || '';
    var hubUrl = root.getAttribute('data-hub-url') || '';
    var quizLabel = root.getAttribute('data-quiz-label') || 'Take the Free Assessment';
    var hubLabel = root.getAttribute('data-hub-label') || 'Explore the Hub';
    var supportEmail = root.getAttribute('data-support-email') || '';
    var supportPhone = root.getAttribute('data-support-phone') || '';
    var supportPhoneDisplay = root.getAttribute('data-support-phone-display') || supportPhone;
    var supportUrl = root.getAttribute('data-support-url') || '';
    var supportLabel = root.getAttribute('data-support-label') || 'Help Center';

    var STATUS_CLASS = 'modular-pdp-ask-ai__status body-small';

    function setStatus(message, type) {
      if (!statusEl) return;
      if (!message) {
        statusEl.hidden = true;
        statusEl.textContent = '';
        statusEl.className = STATUS_CLASS;
        return;
      }
      statusEl.hidden = false;
      statusEl.textContent = message;
      statusEl.className =
        STATUS_CLASS + (type ? ' modular-pdp-ask-ai__status--' + type : '');
    }

    function clearChipSelection() {
      root.querySelectorAll('[data-ask-ai-chip]').forEach(function (c) {
        c.removeAttribute('aria-pressed');
      });
    }

    function setActiveChip(chip) {
      clearChipSelection();
      if (chip) chip.setAttribute('aria-pressed', 'true');
    }

    function setLoading(isLoading) {
      if (submitBtn) submitBtn.disabled = isLoading;
      if (input) input.disabled = isLoading;
      root.querySelectorAll('[data-ask-ai-chip]').forEach(function (c) { c.disabled = isLoading; });
      if (!isLoading) clearChipSelection();
      if (!statusEl) return;
      if (!isLoading) { setStatus('', ''); return; }
      statusEl.hidden = false;
      statusEl.className = STATUS_CLASS + ' modular-pdp-ask-ai__status--loading';
      statusEl.innerHTML =
        '<span class="modular-pdp-ask-ai__thinking">' +
        '<span></span><span></span><span></span>' +
        '</span>' +
        '<span>Thinking…</span>';
    }

    function renderNudges(nudges) {
      if (!nudgesEl) return;
      nudgesEl.innerHTML = '';
      if (!nudges || !nudges.length) {
        nudgesEl.hidden = true;
        return;
      }

      nudges.forEach(function (nudge) {
        if (!nudge || !nudge.type) return;
        var url = nudge.type === 'quiz' ? quizUrl : nudge.type === 'hub' ? hubUrl : '';
        var label = nudge.type === 'quiz' ? quizLabel : nudge.type === 'hub' ? hubLabel : '';
        if (!url || !label) return;

        var link = doc.createElement('a');
        link.className = 'modular-pdp-ask-ai__nudge btn-atc btn-atc--sm btn-atc--outline';
        link.href = url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.textContent = label;
        nudgesEl.appendChild(link);
      });

      nudgesEl.hidden = nudgesEl.childElementCount === 0;
    }

    function renderBlogLink(blogLink) {
      if (!blogLinkEl) return;
      blogLinkEl.innerHTML = '';
      if (!blogLink || !blogLink.url || !blogLink.title) {
        blogLinkEl.hidden = true;
        return;
      }
      var a = doc.createElement('a');
      a.className = 'modular-pdp-ask-ai__blog-link-anchor body-small';
      a.href = blogLink.url;
      a.textContent = blogLink.title;
      blogLinkEl.appendChild(a);
      blogLinkEl.hidden = false;
    }

    function isSupportDeflection(answerText) {
      var a = String(answerText || '').toLowerCase();
      return /customer support|support team|contact our team|reach out to (?:our|us)|help (?:center|desk)|refund policy|return policy|shipping inquiry/.test(a);
    }

    function renderSupportLinks(answerText) {
      if (!supportEl) return;
      supportEl.innerHTML = '';
      if (!isSupportDeflection(answerText) || (!supportEmail && !supportPhone && !supportUrl)) {
        supportEl.hidden = true;
        return;
      }

      var label = doc.createElement('p');
      label.className = 'modular-pdp-ask-ai__support-label body-xs text-muted';
      label.textContent = 'Get in touch:';
      supportEl.appendChild(label);

      var links = doc.createElement('div');
      links.className = 'modular-pdp-ask-ai__support-links';

      if (supportEmail) {
        var emailA = doc.createElement('a');
        emailA.className = 'modular-pdp-ask-ai__support-link body-xs';
        emailA.href = 'mailto:' + supportEmail;
        emailA.textContent = supportEmail;
        emailA.dataset.supportType = 'email';
        links.appendChild(emailA);
      }

      if (supportPhone) {
        var phoneA = doc.createElement('a');
        phoneA.className = 'modular-pdp-ask-ai__support-link body-xs';
        phoneA.href = 'tel:' + supportPhone;
        phoneA.textContent = supportPhoneDisplay || supportPhone;
        phoneA.dataset.supportType = 'phone';
        links.appendChild(phoneA);
      }

      if (supportUrl) {
        var urlA = doc.createElement('a');
        urlA.className = 'modular-pdp-ask-ai__support-link body-xs';
        urlA.href = supportUrl;
        urlA.textContent = supportLabel || 'Help Center';
        urlA.target = '_blank';
        urlA.rel = 'noopener noreferrer';
        urlA.dataset.supportType = 'help';
        links.appendChild(urlA);
      }

      supportEl.appendChild(links);
      supportEl.hidden = links.childElementCount === 0;
    }

    function renderRecommendation(rec) {
      if (!recoEl) return;
      recoEl.innerHTML = '';
      if (!rec || !rec.handle) {
        recoEl.hidden = true;
        return;
      }

      var candidate = candidateByHandle[rec.handle];
      if (!candidate || !candidate.title) {
        recoEl.hidden = true;
        return;
      }

      var url = candidate.url || '/products/' + candidate.handle;
      if (!isProductUrl(url)) {
        recoEl.hidden = true;
        return;
      }

      var label = doc.createElement('p');
      label.className = 'modular-pdp-ask-ai__reco-label body-2xs font-weight-bold text-uppercase text-muted';
      label.textContent = 'Pairs well with';
      recoEl.appendChild(label);

      var card = doc.createElement('article');
      card.className = 'modular-pdp-ask-ai__reco-card';

      if (candidate.image) {
        var imgWrap = doc.createElement('div');
        imgWrap.className = 'modular-pdp-ask-ai__reco-media';
        var img = doc.createElement('img');
        img.className = 'modular-pdp-ask-ai__reco-image';
        img.src = candidate.image;
        img.alt = candidate.title || '';
        img.loading = 'lazy';
        imgWrap.appendChild(img);
        card.appendChild(imgWrap);
      }

      var body = doc.createElement('div');
      body.className = 'modular-pdp-ask-ai__reco-body';

      var title = doc.createElement('h3');
      title.className = 'modular-pdp-ask-ai__reco-title h6';
      title.textContent = candidate.title || rec.handle;
      body.appendChild(title);

      if (candidate.pack) {
        var pack = doc.createElement('p');
        pack.className = 'modular-pdp-ask-ai__reco-pack body-xs text-muted';
        pack.textContent = candidate.pack;
        body.appendChild(pack);
      }

      if (rec.reason) {
        var reason = doc.createElement('p');
        reason.className = 'modular-pdp-ask-ai__reco-reason body-xs';
        reason.textContent = rec.reason;
        body.appendChild(reason);
      }

      if (candidate.price) {
        var price = doc.createElement('p');
        price.className = 'modular-pdp-ask-ai__reco-price body-small font-weight-semibold';
        price.textContent = stripHtml(candidate.price);
        body.appendChild(price);
      }

      var actions = doc.createElement('div');
      actions.className = 'modular-pdp-ask-ai__reco-actions';

      var viewLink = doc.createElement('a');
      viewLink.className = 'modular-pdp-ask-ai__reco-link btn-atc btn-atc--sm btn-atc--outline';
      viewLink.href = url;
      viewLink.textContent = 'View product';
      actions.appendChild(viewLink);

      if (candidate.variant_id) {
        var atcBtn = doc.createElement('button');
        atcBtn.type = 'button';
        atcBtn.className = 'modular-pdp-ask-ai__reco-atc btn-atc btn-atc--sm js-add-to-cart';
        atcBtn.textContent = 'Add to cart';
        atcBtn.dataset.variantId = String(candidate.variant_id);
        atcBtn.dataset.productTitle = candidate.title || '';
        atcBtn.addEventListener('click', function () {
          pushEvent('ask_ai_reco_added_to_cart', { handle: candidate.handle });
        });
        actions.appendChild(atcBtn);
      }

      body.appendChild(actions);
      card.appendChild(body);
      recoEl.appendChild(card);
      recoEl.hidden = false;
    }

    function showResponse(payload, isDemo, question) {
      if (!responseWrap || !answerEl) return;
      responseWrap.hidden = false;
      if (responseLabel) {
        responseLabel.textContent = isDemo
          ? 'Demo mode answer (connect endpoint for live Claude output).'
          : 'Answer generated.';
      }
      if (questionEl) questionEl.textContent = question ? '“' + question + '”' : '';
      answerEl.innerHTML = formatAnswer(payload.answer || '');
      renderBlogLink(payload.blogLink || null);
      renderSupportLinks(payload.answer || '');
      renderRecommendation((payload.recommendations || [])[0]);
      renderNudges(payload.nudges || []);
      pushEvent('ask_ai_answer_shown', { demo: !!isDemo });
    }

    function askQuestion(question) {
      var q = String(question || '').trim();
      if (!q) return;

      // Daily limit — max SESSION_LIMIT questions per 24-hour window (localStorage, persists across tabs/refreshes)
      var session = getSessionData();
      if (session.count >= SESSION_LIMIT) {
        setStatus('You\'ve reached the daily question limit. Come back tomorrow to continue.', 'error');
        return;
      }

      // Interval throttle — prevent rapid-fire submissions
      var now = Date.now();
      if (now - lastAskTime < MIN_INTERVAL_MS) {
        setStatus('Please wait a moment before asking another question.', 'error');
        return;
      }
      lastAskTime = now;
      session.count++;
      saveSessionData(session);

      setLoading(true);
      if (input) input.value = '';
      if (responseWrap) responseWrap.hidden = true;
      pushEvent('ask_ai_submitted', { question: q });

      var productPayload = {
        productTitle: root.getAttribute('data-product-title') || '',
        productHandle: root.getAttribute('data-product-handle') || '',
        productVendor: root.getAttribute('data-product-vendor') || '',
        productType: root.getAttribute('data-product-type') || '',
        variantTitle: root.getAttribute('data-variant-title') || '',
        variantId: root.getAttribute('data-variant-id') || '',
        price: root.getAttribute('data-price') || '',
        currency: root.getAttribute('data-currency') || '',
        tags: root.getAttribute('data-tags') || '',
        description: root.getAttribute('data-description') || '',
      };

      var useLive = !!endpoint;
      if (!useLive) {
        var demoPayload = {
          answer: demoAnswer(q, productPayload.productTitle),
          recommendations: [],
          nudges: [],
        };
        if (wantsPairingIntent(q) && candidates[0]) {
          demoPayload.recommendations = [
            {
              handle: candidates[0].handle,
              reason: 'This is a strong complementary option for this question.',
            },
          ];
        }
        showResponse(demoPayload, true, q);
        setLoading(false);
        return;
      }

      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          question: q,
          product: productPayload,
          relatedCandidates: candidates,
          source: 'shopify_pdp_ask_ai',
        }),
      })
        .then(function (res) {
          if (!res.ok) throw new Error(String(res.status));
          return res.json();
        })
        .then(function (data) {
          var answer =
            data.answer || data.output || data.response || 'I could not generate an answer right now.';

          // Merge worker-returned product details into candidateByHandle
          // so recommendation cards render even without section settings
          var details = Array.isArray(data.recommendationDetails) ? data.recommendationDetails : [];
          details.forEach(function (d) {
            if (d && d.handle && !candidateByHandle[d.handle]) {
              candidateByHandle[d.handle] = d;
            }
          });

          showResponse(
            {
              answer: answer,
              recommendations: data.recommendations || [],
              nudges: data.nudges || [],
              blogLink: data.blogLink || null,
            },
            false,
            q
          );
        })
        .catch(function (err) {
          var code = parseInt(err && err.message, 10);
          var msg = code >= 500 || code === 502 || code === 503
            ? 'Our AI is a little busy right now — please try again in a few minutes.'
            : code === 429
              ? 'Too many requests — please wait a moment and try again.'
              : 'Could not connect. Check your connection and try again.';
          setStatus(msg, 'error');
        })
        .finally(function () {
          setLoading(false);
        });
    }

    root.querySelectorAll('[data-ask-ai-chip]').forEach(function (chip) {
      chip.addEventListener('click', function () {
        var text = chip.textContent.trim();
        if (input) input.value = text;
        setActiveChip(chip);
        pushEvent('ask_ai_chip_clicked', { chip: text });
        askQuestion(text);
      });
    });

    if (form) {
      form.addEventListener('submit', function (event) {
        event.preventDefault();
        clearChipSelection();
        askQuestion(input ? input.value : '');
      });
    }
  }

  function initAll() {
    doc.querySelectorAll('[data-pdp-ask-ai]').forEach(initWidget);
  }

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  doc.addEventListener('shopify:section:load', initAll);
})((typeof window !== 'undefined' && window) || this, document);
